/**
 * ADR-213 — the pure rules of the personal offer (no DOM, no network).
 * Unit-tested: scripts/tests/eros-offer.test.mjs. eros-offer.js (state, landing,
 * quote) and eros-offer-render.js (drawing) both read them from
 * window.ErosOfferRules.
 */
(function (root) {
  'use strict';

  var STORE_KEY = 'eros_offer_v1';
  var TOKEN_RE = /^[A-Za-z0-9_-]{16,128}$/;
  var REASONS = {
    expired: 1,
    used: 1,
    other_customer: 1,
    sign_in: 1,
    not_found: 1,
    products_not_in_cart: 1,
    delivery_only: 1
  };
  /* reasons the customer can still fix: the pill keeps counting */
  var LIVE_REASONS = { sign_in: 1, products_not_in_cart: 1 };
  var GID_PREFIX = 'gid://shopify/ProductVariant/';

  function isValidToken(t) {
    return typeof t === 'string' && TOKEN_RE.test(t);
  }

  function toMs(v) {
    if (typeof v === 'number' && isFinite(v)) return v;
    if (typeof v === 'string' && v) {
      var n = Date.parse(v);
      return isNaN(n) ? null : n;
    }
    return null;
  }

  /** The stored value, or null when it is malformed or already past its end. */
  function parseStored(raw, now) {
    var data;
    try {
      data = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!data || !isValidToken(data.token)) return null;
    var exp = toMs(data.expiresAt);
    if (exp !== null && exp <= now) return null;
    return {
      token: data.token,
      expiresAt: exp,
      state: typeof data.state === 'string' && REASONS[data.state] ? data.state : null,
      lines: Array.isArray(data.lines) ? cleanLines(data.lines) : null
    };
  }

  /**
   * The record kept in storage from a preview answer (lines only while the offer is live).
   * ADR-218: an offer opened by another account keeps nothing (null): its products are
   * added as a plain cart link, with no token, no line and no message.
   */
  function recordFromPreview(token, preview, now, fallbackTtlMs) {
    if (preview.state === 'other_customer') return null;
    var live = preview.state === 'valid' || preview.state === 'sign_in';
    var exp = live ? toMs(preview.expires_at) : null;
    return {
      token: token,
      expiresAt: exp === null ? now + fallbackTtlMs : exp,
      state: preview.state === 'valid' ? null : reasonKey(preview.state),
      lines: live ? cleanLines(preview.lines) : []
    };
  }

  /** True when the stored state can still change (none, sign_in, products_not_in_cart). */
  function isLiveState(state) {
    return !state || !!LIVE_REASONS[state];
  }

  /** Preview lines kept in the stored record: only well-formed ones, in order. */
  function cleanLines(lines) {
    return (lines || [])
      .filter(function (l) {
        return (
          l &&
          typeof l.variantGid === 'string' &&
          (l.kind === 'percent' || l.kind === 'amount') &&
          Number(l.value) > 0 &&
          Number(l.maxQuantity) >= 1
        );
      })
      .map(function (l) {
        return {
          variantGid: l.variantGid,
          kind: l.kind,
          value: Number(l.value),
          maxQuantity: Math.floor(Number(l.maxQuantity))
        };
      });
  }

  /**
   * ADR-213 §2.6 — the signed-out estimate. The same rule as the app, pinned by
   * design/offers/offer_estimate_cases.json. offerLines = the preview's lines;
   * cartItems = [{variantGid, quantity, unitIqd, compareAtIqd}] in whole dinars.
   * Per unit: percent = round(unit * value / 100); amount = min(value, unit),
   * for up to maxQuantity units. Variants not in the offer are never touched.
   */
  function estimateOffer(offerLines, cartItems) {
    var byVariant = {};
    (offerLines || []).forEach(function (l) {
      if (l && !byVariant[l.variantGid]) byVariant[l.variantGid] = l;
    });
    var lines = [];
    var total = 0;
    (cartItems || []).forEach(function (it) {
      var o = byVariant[it.variantGid];
      var unit = Number(it.unitIqd);
      if (!o || !isFinite(unit) || unit <= 0) return;
      var qty = Math.min(Math.floor(Number(it.quantity) || 0), Number(o.maxQuantity));
      if (!(qty > 0)) return;
      var per = o.kind === 'percent' ? Math.round((unit * Number(o.value)) / 100) : Math.min(Number(o.value), unit);
      if (!(per > 0)) return;
      total += per * qty;
      lines.push({
        variantGid: it.variantGid,
        kind: o.kind,
        value: Number(o.value),
        discountedQuantity: qty,
        unitIqd: unit,
        discountedUnitIqd: unit - per,
        lineDiscountIqd: per * qty,
        originalUnitIqd: Math.max(unit, Number(it.compareAtIqd) || 0)
      });
    });
    return { applies: lines.length > 0, discountIqd: total, lines: lines };
  }

  /** /cart.js items → estimate cart items (cart.js prices are minor units ×100). */
  function estimateItems(items) {
    return (items || []).map(function (it) {
      return {
        variantGid: GID_PREFIX + it.variant_id,
        quantity: it.quantity,
        unitIqd: Math.round(Number(it.price) / 100),
        compareAtIqd: null /* cart.js carries none; the cart row's data-compare-at-iqd does (render) */
      };
    });
  }

  /** mm:ss, or hh:mm:ss above one hour. Never negative. */
  function formatCountdown(ms) {
    var total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    function two(n) {
      return n < 10 ? '0' + n : String(n);
    }
    return h > 0 ? h + ':' + two(m) + ':' + two(s) : two(m) + ':' + two(s);
  }

  /** Quote lines from /cart.js items. */
  function quoteLines(items) {
    return (items || []).map(function (it) {
      return { variantGid: GID_PREFIX + it.variant_id, quantity: it.quantity };
    });
  }

  /** The exact cart as the quote sees it: token + sorted variant:qty:gift-mark triples (study 15: a paid
   *  line turned into a `_eros_gift` line of the same variant and quantity is a new cart). */
  function cartSignature(items, token) {
    var parts = (items || [])
      .map(function (it) {
        var gift = it && it.properties ? it.properties._eros_gift : null;
        return String(it.variant_id) + ':' + it.quantity + ':' + (typeof gift === 'string' ? gift : '');
      })
      .sort();
    return String(token || '') + '|' + parts.join(',');
  }

  /** Any unknown reason reads as «not found», never as a blank line. */
  function reasonKey(reason) {
    return typeof reason === 'string' && REASONS[reason] ? reason : 'not_found';
  }

  function variantTail(gid) {
    var s = String(gid || '');
    return s.indexOf(GID_PREFIX) === 0 ? s.slice(GID_PREFIX.length) : s;
  }

  /** What one cart line shows: chip (percent or amount), old and new unit price. */
  function lineView(l) {
    if (!l || (l.kind !== 'percent' && l.kind !== 'amount')) return null;
    var unit = Number(l.unitIqd);
    var discounted = Number(l.discountedUnitIqd);
    if (!isFinite(unit) || !isFinite(discounted) || !(Number(l.discountedQuantity) > 0)) return null;
    return {
      variantId: variantTail(l.variantGid),
      chip: { kind: l.kind, value: Number(l.value) },
      unitIqd: unit,
      discountedUnitIqd: discounted,
      discountedQuantity: Number(l.discountedQuantity),
      lineDiscountIqd: Math.round(Number(l.lineDiscountIqd) || 0)
    };
  }

  function lineViews(offer) {
    if (!offer || !offer.applies || !Array.isArray(offer.lines)) return [];
    return offer.lines.map(lineView).filter(Boolean);
  }

  /** ADR-224: a quote's delivery part — {kind:'free'} | {kind:'amount', value} — or null. */
  function parseShipping(raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (raw.kind === 'free') return { kind: 'free' };
    var v = Number(raw.value);
    return raw.kind === 'amount' && isFinite(v) && v > 0 ? { kind: 'amount', value: v } : null;
  }

  /** ADR-224: the delivery fee after the offer, as the worker prices the order's line. */
  function discountedShippingIqd(feeIqd, shipping) {
    if (!shipping) return feeIqd;
    return shipping.kind === 'free' ? 0 : Math.max(0, feeIqd - shipping.value);
  }

  /** The discount line from a quote's `offer` object (cart and checkout share it). */
  function viewFromOffer(offer, fallbackExpiresAt) {
    if (!offer) return null;
    var exp = toMs(offer.expiresAt);
    if (exp === null) exp = fallbackExpiresAt === undefined ? null : fallbackExpiresAt;
    if (offer.applies) {
      var view = { kind: 'applies', amountIqd: Math.round(Number(offer.discountIqd) || 0), expiresAt: exp };
      var shipping = parseShipping(offer.shipping);
      if (shipping) view.shipping = shipping;
      return view;
    }
    var key = reasonKey(offer.reason);
    return { kind: 'reason', key: key, expiresAt: LIVE_REASONS[key] ? exp : null };
  }

  /**
   * The discount line for the cart. s = { token, expiresAt, storedState,
   * signedIn, quote, failed, ended, lines, estimate }. Null means: draw no line.
   * Signed out with stored preview lines, the estimate (estimateOffer) stands in
   * for the quote; stored dead states still show their reason.
   */
  function viewFromState(s, now) {
    if (!s.token) return s.ended ? { kind: 'ended' } : null;
    if (s.expiresAt !== null && s.expiresAt <= now) return { kind: 'ended' };
    if (!s.signedIn) {
      var stored = s.storedState && !LIVE_REASONS[s.storedState] ? s.storedState : 'sign_in';
      if (stored === 'sign_in' && s.lines && s.lines.length && !s.failed) {
        if (!s.estimate) return { kind: 'pending', expiresAt: s.expiresAt };
        return viewFromOffer({ applies: s.estimate.applies, discountIqd: s.estimate.discountIqd, reason: 'products_not_in_cart' }, s.expiresAt);
      }
      return { kind: 'reason', key: stored, expiresAt: LIVE_REASONS[stored] ? s.expiresAt : null };
    }
    if (s.quote && s.quote.offer) return viewFromOffer(s.quote.offer, s.expiresAt);
    return s.failed ? null : { kind: 'pending', expiresAt: s.expiresAt };
  }

  /** ADR-218: a server quote that says the offer belongs to another account (the token is dropped silently). */
  function isOtherCustomerQuote(quote) {
    var o = quote && quote.offer;
    return !!o && !o.applies && o.reason === 'other_customer';
  }

  /** Variants the link should add: not already in the cart, in offer order (ADR-218: also for another account's offer). */
  function variantsToAdd(preview, cartVariantIds) {
    if (!preview || preview.add_to_cart !== true) return [];
    if (preview.state !== 'valid' && preview.state !== 'sign_in' && preview.state !== 'other_customer') return [];
    var have = {};
    (cartVariantIds || []).forEach(function (id) {
      have[String(id)] = true;
    });
    var out = [];
    (preview.lines || []).forEach(function (l) {
      var id = variantTail(l && l.variantGid);
      if (/^\d{1,20}$/.test(id) && !have[id] && out.indexOf(id) === -1) out.push(id);
    });
    return out;
  }

  var api = {
    STORE_KEY: STORE_KEY,
    isValidToken: isValidToken,
    toMs: toMs,
    parseStored: parseStored,
    cleanLines: cleanLines,
    recordFromPreview: recordFromPreview,
    isLiveState: isLiveState,
    estimateOffer: estimateOffer,
    estimateItems: estimateItems,
    formatCountdown: formatCountdown,
    quoteLines: quoteLines,
    cartSignature: cartSignature,
    reasonKey: reasonKey,
    lineView: lineView,
    lineViews: lineViews,
    parseShipping: parseShipping,
    discountedShippingIqd: discountedShippingIqd,
    viewFromOffer: viewFromOffer,
    viewFromState: viewFromState,
    variantsToAdd: variantsToAdd,
    isOtherCustomerQuote: isOtherCustomerQuote
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.ErosOfferRules = api;
})(typeof window !== 'undefined' ? window : globalThis);
